/**
 * BuildingResidentialCriteriaPanel — V1.6
 *
 * Panel colapsable para configurar los criterios de inclusión residencial de un proyecto.
 * Vive en la pestaña Edificios del sidebar izquierdo, debajo del toggle de visibilidad.
 *
 * Estructura:
 * 1. Cabecera colapsable con estado (usando defaults / configurado + fecha)
 * 2. Sección de función urbana (checkboxes)
 * 3. Sección de umbrales de exclusión por área (toggle + input numérico)
 * 4. Sección de otros criterios (asociación, masa)
 * 5. Preview de elegibilidad (carga lazy al abrir)
 * 6. Botón "Guardar criterios"
 */

import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, Save } from 'lucide-react';
import type {
  BuildingResidentialCriteria,
  PatchResidentialCriteriaDto,
  ResidentialCriteriaPreview,
} from './building-residential-criteria.types';
import { DEFAULT_RESIDENTIAL_CRITERIA } from './building-residential-criteria.types';
import { residentialCriteriaApi } from './building-residential-criteria.api';
import '../layouts/panels/LeftPanel/tabbed-left-panel.css';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  projectId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    });
  } catch {
    return '';
  }
}

function pct(n: number, total: number): string {
  if (total === 0) return '—';
  return `${((n / total) * 100).toFixed(1)}%`;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function BuildingResidentialCriteriaPanel({ projectId }: Props): JSX.Element {
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // Criterios actualmente en servidor
  const [serverCriteria, setServerCriteria] = useState<BuildingResidentialCriteria | null>(null);
  const [isDefault, setIsDefault] = useState(true);

  // Criterios en edición local (antes de guardar)
  const [draft, setDraft] = useState<BuildingResidentialCriteria | null>(null);

  // Preview de elegibilidad
  const [preview,        setPreview]        = useState<ResidentialCriteriaPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Carga inicial al abrir ─────────────────────────────────────────────────

  const loadCriteria = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await residentialCriteriaApi.getCriteria(projectId);
      setServerCriteria(res.criteria);
      setIsDefault(res.isDefault);
      setDraft(res.criteria);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando criterios');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && serverCriteria == null) {
      loadCriteria();
    }
  }, [open, serverCriteria, loadCriteria]);

  // ── Preview ───────────────────────────────────────────────────────────────

  const loadPreview = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    setPreviewLoading(true);
    try {
      const p = await residentialCriteriaApi.getPreview(projectId);
      setPreview(p);
    } catch {
      // preview failure is non-fatal
    } finally {
      setPreviewLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && draft != null && preview == null) {
      loadPreview();
    }
  }, [open, draft, preview, loadPreview]);

  // ── Edición del draft ─────────────────────────────────────────────────────

  const patchDraft = (patch: Partial<BuildingResidentialCriteria>): void => {
    setDraft((prev) => prev ? { ...prev, ...patch } : prev);
  };

  // ── Guardar ───────────────────────────────────────────────────────────────

  const handleSave = async (): Promise<void> => {
    if (!draft || !projectId) return;
    setSaving(true);
    setError(null);
    try {
      const dto: PatchResidentialCriteriaDto = {
        includeUnclassifiedAsResidential: draft.includeUnclassifiedAsResidential,
        includeResidential:               draft.includeResidential,
        includeMixed:                     draft.includeMixed,
        excludeNonResidential:            draft.excludeNonResidential,
        excludeByFootprintArea:           draft.excludeByFootprintArea,
        maxResidentialFootprintAreaM2:    draft.maxResidentialFootprintAreaM2,
        excludeByBuiltArea:               draft.excludeByBuiltArea,
        maxResidentialBuiltAreaM2:        draft.maxResidentialBuiltAreaM2,
        excludeUnassociated:              draft.excludeUnassociated,
        excludeWithoutEstimatedMass:      draft.excludeWithoutEstimatedMass,
      };
      const res = await residentialCriteriaApi.patchCriteria(projectId, dto);
      setServerCriteria(res.criteria);
      setIsDefault(res.isDefault);
      setDraft(res.criteria);
      // Refresh preview after save
      setPreview(null);
      loadPreview();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error guardando criterios');
    } finally {
      setSaving(false);
    }
  };

  // ── Resetear al default ───────────────────────────────────────────────────

  const handleReset = (): void => {
    const defaults: BuildingResidentialCriteria = {
      ...DEFAULT_RESIDENTIAL_CRITERIA,
      updatedAt: new Date().toISOString(),
      updatedBy: null,
    };
    setDraft(defaults);
  };

  // ── Dirty check ───────────────────────────────────────────────────────────

  const isDirty = draft != null && serverCriteria != null && (
    draft.includeUnclassifiedAsResidential !== serverCriteria.includeUnclassifiedAsResidential ||
    draft.includeResidential               !== serverCriteria.includeResidential               ||
    draft.includeMixed                     !== serverCriteria.includeMixed                     ||
    draft.excludeNonResidential            !== serverCriteria.excludeNonResidential            ||
    draft.excludeByFootprintArea           !== serverCriteria.excludeByFootprintArea           ||
    draft.maxResidentialFootprintAreaM2    !== serverCriteria.maxResidentialFootprintAreaM2    ||
    draft.excludeByBuiltArea               !== serverCriteria.excludeByBuiltArea               ||
    draft.maxResidentialBuiltAreaM2        !== serverCriteria.maxResidentialBuiltAreaM2        ||
    draft.excludeUnassociated              !== serverCriteria.excludeUnassociated              ||
    draft.excludeWithoutEstimatedMass      !== serverCriteria.excludeWithoutEstimatedMass
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="brc-panel">

      {/* Cabecera colapsable */}
      <button
        type="button"
        className={`brc-header${open ? ' is-open' : ''}${!isDefault ? ' has-config' : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="brc-header-title">Criterios residenciales</span>
        <span className="brc-header-meta">
          {isDefault
            ? <span className="brc-tag brc-tag--default">Por defecto</span>
            : <span className="brc-tag brc-tag--custom">Configurado {formatDate(serverCriteria?.updatedAt)}</span>
          }
        </span>
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {/* Contenido */}
      {open && (
        <div className="brc-body">
          {loading && <div className="brc-loading">Cargando criterios…</div>}
          {error   && <div className="brc-error">{error}</div>}

          {draft != null && !loading && (
            <>
              {/* 1. Función urbana */}
              <div className="brc-section">
                <span className="brc-section-title">Función urbana</span>
                <div className="brc-checks">
                  <BrcCheck
                    label="Incluir residencial"
                    checked={draft.includeResidential}
                    onChange={(v) => patchDraft({ includeResidential: v })}
                  />
                  <BrcCheck
                    label="Incluir mixto"
                    checked={draft.includeMixed}
                    onChange={(v) => patchDraft({ includeMixed: v })}
                  />
                  <BrcCheck
                    label="Incluir sin clasificar"
                    checked={draft.includeUnclassifiedAsResidential}
                    onChange={(v) => patchDraft({ includeUnclassifiedAsResidential: v })}
                  />
                  <BrcCheck
                    label="Excluir no residencial"
                    checked={draft.excludeNonResidential}
                    onChange={(v) => patchDraft({ excludeNonResidential: v })}
                  />
                </div>
              </div>

              {/* 2. Umbral por huella */}
              <div className="brc-section">
                <span className="brc-section-title">Límites de área</span>
                <div className="brc-threshold-row">
                  <BrcCheck
                    label="Excluir huella >"
                    checked={draft.excludeByFootprintArea}
                    onChange={(v) => patchDraft({ excludeByFootprintArea: v })}
                  />
                  <BrcNumInput
                    value={draft.maxResidentialFootprintAreaM2}
                    disabled={!draft.excludeByFootprintArea}
                    unit="m²"
                    onChange={(v) => patchDraft({ maxResidentialFootprintAreaM2: v })}
                  />
                </div>
                <div className="brc-threshold-row">
                  <BrcCheck
                    label="Excluir masa >"
                    checked={draft.excludeByBuiltArea}
                    onChange={(v) => patchDraft({ excludeByBuiltArea: v })}
                  />
                  <BrcNumInput
                    value={draft.maxResidentialBuiltAreaM2}
                    disabled={!draft.excludeByBuiltArea}
                    unit="m²"
                    onChange={(v) => patchDraft({ maxResidentialBuiltAreaM2: v })}
                  />
                </div>
              </div>

              {/* 3. Otros criterios */}
              <div className="brc-section">
                <span className="brc-section-title">Otros criterios</span>
                <div className="brc-checks">
                  <BrcCheck
                    label="Excluir sin asociación a red"
                    checked={draft.excludeUnassociated}
                    onChange={(v) => patchDraft({ excludeUnassociated: v })}
                  />
                  <BrcCheck
                    label="Excluir sin masa estimada"
                    checked={draft.excludeWithoutEstimatedMass}
                    onChange={(v) => patchDraft({ excludeWithoutEstimatedMass: v })}
                  />
                </div>
              </div>

              {/* 4. Preview */}
              <div className="brc-section">
                <div className="brc-preview-header">
                  <span className="brc-section-title">Preview de elegibilidad</span>
                  <button
                    type="button"
                    className="brc-preview-refresh"
                    onClick={() => { setPreview(null); loadPreview(); }}
                    disabled={previewLoading}
                    title="Recalcular preview"
                  >
                    <RefreshCw size={10} className={previewLoading ? 'brc-spin' : ''} />
                  </button>
                </div>
                {previewLoading && <div className="brc-loading">Calculando…</div>}
                {!previewLoading && preview != null && (
                  <div className="brc-preview-grid">
                    <span className="brc-prev-key">Total edificios</span>
                    <span className="brc-prev-val">{preview.totalBuildings.toLocaleString('es-AR')}</span>

                    <span className="brc-prev-key brc-prev-key--eligible">Auto elegible</span>
                    <span className="brc-prev-val brc-prev-val--eligible">
                      {preview.autoEligible.toLocaleString('es-AR')}
                      <span className="brc-prev-pct">{pct(preview.autoEligible, preview.totalBuildings)}</span>
                    </span>

                    <span className="brc-prev-key">Auto excluido</span>
                    <span className="brc-prev-val">
                      {preview.autoExcluded.toLocaleString('es-AR')}
                      <span className="brc-prev-pct">{pct(preview.autoExcluded, preview.totalBuildings)}</span>
                    </span>

                    {preview.manualIncluded > 0 && (
                      <>
                        <span className="brc-prev-key brc-prev-key--manual">Manual incluido</span>
                        <span className="brc-prev-val brc-prev-val--manual">{preview.manualIncluded.toLocaleString('es-AR')}</span>
                      </>
                    )}
                    {preview.manualExcluded > 0 && (
                      <>
                        <span className="brc-prev-key brc-prev-key--manual">Manual excluido</span>
                        <span className="brc-prev-val brc-prev-val--manual">{preview.manualExcluded.toLocaleString('es-AR')}</span>
                      </>
                    )}

                    <span className="brc-prev-key brc-prev-key--effective">Efectivo elegible</span>
                    <span className="brc-prev-val brc-prev-val--effective">
                      {preview.effectiveEligible.toLocaleString('es-AR')}
                      <span className="brc-prev-pct">{pct(preview.effectiveEligible, preview.totalBuildings)}</span>
                    </span>
                  </div>
                )}
                {!previewLoading && preview == null && (
                  <div className="brc-loading">Preview no disponible</div>
                )}
              </div>

              {/* 5. Acciones */}
              <div className="brc-actions">
                <button
                  type="button"
                  className="brc-btn-reset"
                  onClick={handleReset}
                  title="Restaurar criterios por defecto"
                >
                  Defaults
                </button>
                <button
                  type="button"
                  className={`brc-btn-save${isDirty ? ' is-dirty' : ''}`}
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  title={isDirty ? 'Guardar cambios' : 'Sin cambios'}
                >
                  <Save size={11} />
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function BrcCheck({
  label, checked, onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }): JSX.Element {
  return (
    <label className="brc-check-row">
      <input
        type="checkbox"
        className="brc-checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="brc-check-label">{label}</span>
    </label>
  );
}

function BrcNumInput({
  value, disabled, unit, onChange,
}: {
  value:    number | null;
  disabled: boolean;
  unit:     string;
  onChange: (v: number | null) => void;
}): JSX.Element {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value.trim();
    if (raw === '' || raw === '0') { onChange(null); return; }
    const n = parseFloat(raw);
    if (!isNaN(n) && n > 0) onChange(n);
  };

  return (
    <div className={`brc-num-wrap${disabled ? ' is-disabled' : ''}`}>
      <input
        type="number"
        className="brc-num-input"
        value={value ?? ''}
        min={1}
        step={50}
        disabled={disabled}
        placeholder="—"
        onChange={handleChange}
      />
      <span className="brc-num-unit">{unit}</span>
    </div>
  );
}
